import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { fetchContent, fetchBranding, fetchMenu, fetchProducts, getImageUrl } from '../api/api';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';
import { useCompare } from '../context/CompareContext';
import { useCurrency } from '../context/CurrencyContext';
import SiteSearch from './SiteSearch';
import './Header.css';

function ToplineRotator({ items }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!items || items.length <= 1) return;
    const timer = setInterval(() => setIdx(i => (i + 1) % items.length), 3500);
    return () => clearInterval(timer);
  }, [items]);
  if (!items || items.length === 0) return null;
  return (
    <div className="topline-rotator">
      <div className="custom-text-item" dangerouslySetInnerHTML={{ __html: items[idx] }} />
    </div>
  );
}

const NavLink = ({ path, children, onClick }) =>
  path.startsWith('http') ? (
    <a href={path} target="_blank" rel="noopener noreferrer" onClick={onClick}>{children}</a>
  ) : (
    <Link to={path} onClick={onClick}>{children}</Link>
  );

function CartDrawer({ open, onClose }) {
  const { cart, removeFromCart, updateQty, subtotal } = useCart();
  const currency = useCurrency();
  const navigate = useNavigate();

  const imgSrc = (item) => {
    const url = item.images?.[0] || item.image || '';
    if (!url) return '/images/scooter-1.jpg';
    return getImageUrl(url);
  };

  return (
    <>
      <div className={`cart-drawer-backdrop ${open ? 'open' : ''}`} onClick={onClose} />
      <div className={`cart-drawer ${open ? 'open' : ''}`}>
        <div className="cart-drawer-header">
          <p className="cart-drawer-title">Your Cart</p>
          <button className="cart-drawer-close" onClick={onClose}>&#10005;</button>
        </div>
        {cart.length === 0 ? (
          <div className="cart-drawer-empty">
            <span></span>
            <p>Your cart is empty</p>
            <button className="btn" onClick={onClose}>Continue Shopping</button>
          </div>
        ) : (
          <>
            <div className="cart-drawer-items">
              {cart.map(item => (
                <div key={item._id} className="cart-drawer-item">
                  <img src={imgSrc(item)} alt={item.name} onError={e => { e.target.src = '/images/scooter-1.jpg'; }} />
                  <div className="cdi-info">
                    <span className="cdi-name">{item.name}</span>
                    <span className="cdi-price">{currency}{item.price.toFixed(2)}</span>
                    <div className="cdi-qty">
                      <button onClick={() => updateQty(item._id, item.qty - 1)}>&#8722;</button>
                      <span>{item.qty}</span>
                      <button onClick={() => updateQty(item._id, item.qty + 1)}>+</button>
                    </div>
                  </div>
                  <div className="cdi-right">
                    <button className="cdi-remove" onClick={() => removeFromCart(item._id + (item.selectedColor?.name || ''))}>&#10005;</button>
                    <span className="cdi-subtotal">{currency}{(item.price * item.qty).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="cart-drawer-footer">
              <div className="cart-drawer-subtotal">
                <span>Subtotal</span>
                <strong>{currency}{subtotal.toFixed(2)}</strong>
              </div>
              <button className="btn cart-drawer-checkout" onClick={() => { onClose(); navigate('/cart'); }}>
                View Cart &amp; Checkout
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openItems, setOpenItems] = useState({});
  const [openGroups, setOpenGroups] = useState({});
  const [menu, setMenu] = useState([]);
  const [navProducts, setNavProducts] = useState([]);
  const [topline, setTopline] = useState([]);
  const [logo, setLogo] = useState('');
  const [siteName, setSiteName] = useState('Green Wheels Scooters');
  const [headerIcons, setHeaderIcons] = useState({ showCompare: true, showUser: true, showCart: true, showSearch: true });
  const [searchOpen, setSearchOpen] = useState(false);
  const { itemCount, cartDrawerOpen, openCartDrawer, closeCartDrawer } = useCart();
  const { user, logout } = useUser();
  const { compareList } = useCompare();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  // Responsive nav overflow: rather than switching the whole nav to the
  // hamburger drawer once it no longer fits inline, only the items that
  // don't fit collapse into a "More" dropdown — everything else stays
  // visible at whatever size the viewport actually allows.
  const navContainerRef = useRef(null);
  const logoRef = useRef(null);
  const actionsRef = useRef(null);
  const measureRef = useRef(null);
  const moreMeasureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(null);

  const recalcNavOverflow = () => {
    if (!navContainerRef.current || !measureRef.current || menu.length === 0) return;
    const totalWidth = navContainerRef.current.clientWidth;
    const logoWidth = logoRef.current?.offsetWidth || 0;
    const actionsWidth = actionsRef.current?.offsetWidth || 0;
    const moreWidth = moreMeasureRef.current?.offsetWidth || 0;
    const safetyMargin = 24; // header-right / header-actions gaps
    const budget = totalWidth - logoWidth - actionsWidth - moreWidth - safetyMargin;

    const itemEls = Array.from(measureRef.current.children);
    let used = 0, count = 0;
    for (let i = 0; i < itemEls.length; i++) {
      const w = itemEls[i].offsetWidth;
      if (used + w > budget) break;
      used += w;
      count++;
    }
    setVisibleCount(count >= itemEls.length ? null : count);
  };

  useLayoutEffect(() => {
    recalcNavOverflow();
    const ro = new ResizeObserver(() => recalcNavOverflow());
    if (navContainerRef.current) ro.observe(navContainerRef.current);
    window.addEventListener('resize', recalcNavOverflow);
    return () => { ro.disconnect(); window.removeEventListener('resize', recalcNavOverflow); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, logo, headerIcons]);   // headerIcons: a new icon changes how much room the nav has

  // close the search bar whenever the page changes
  useEffect(() => { setSearchOpen(false); }, [location.pathname, location.search]);

  useEffect(() => {
    fetchContent('header').then(data => {
      if (!data) return;
      setTopline(data.topline || []);
      setHeaderIcons({
        showCompare: data.showCompare !== false,
        showUser:    data.showUser    !== false,
        showCart:    data.showCart    !== false,
        showSearch:  data.showSearch  !== false,
      });
    }).catch(() => {});
    fetchBranding().then(b => {
      if (b.siteName) setSiteName(b.siteName);
      if (b.logo) setLogo(getImageUrl(b.logo, 220));
    }).catch(() => {});
    fetchMenu().then(items => { if (items.length > 0) setMenu(items); }).catch(() => {});
  }, []);

  // Menu items with an `autoCategory` list that category's products in their dropdown (Admin → Navigation)
  const needsProducts = menu.some(m => m.autoCategory);
  useEffect(() => {
    if (!needsProducts || navProducts.length) return;
    fetchProducts().then(list => setNavProducts(Array.isArray(list) ? list : [])).catch(() => {});
  }, [needsProducts, navProducts.length]);

  // Manual submenu entries first, then auto-listed products (skipping any already linked manually)
  const getSubmenu = (item) => {
    const manual = item.submenu || [];
    if (!item.autoCategory) return manual;
    const used = new Set(manual.map(s => s.path));
    const auto = navProducts
      .filter(p => p.category === item.autoCategory)
      .map(p => ({ name: p.name, path: `/products/${p.category}/${p.model}` }))
      .filter(s => !used.has(s.path));
    return [...manual, ...auto];
  };

  const closeAll = () => { setMobileOpen(false); setOpenItems({}); setOpenGroups({}); };
  const toggleItem = (e, idx) => { e.preventDefault(); e.stopPropagation(); setOpenItems(prev => ({ ...prev, [idx]: !prev[idx] })); };
  const toggleGroup = (e, key) => { e.preventDefault(); e.stopPropagation(); setOpenGroups(prev => ({ ...prev, [key]: !prev[key] })); };

  // Shared markup for one top-level desktop nav item — used both for the
  // real visible row and the hidden measurement clone (so widths match).
  const renderTopNavItem = (item, idx) => {
    const subs = getSubmenu(item);
    const hasSub = subs.length > 0;
    return (
      <div key={idx} className={`nav-item${hasSub ? ' has-submenu' : ''}`}>
        <div className="nav-item-row">
          <NavLink path={item.path} onClick={closeAll}>{item.name}</NavLink>
        </div>
        {hasSub && (
          <div className={`submenu${subs.length > 4 ? ' submenu-mega' : ''}`} style={subs.length > 4 ? { '--sub-rows': Math.ceil(subs.length / 2) } : undefined}>
            {subs.map((sub, i) =>
              sub.children?.length > 0 ? (
                <div key={i} className="submenu-group">
                  <div className="submenu-group-label"><span>{sub.name}</span></div>
                  <div className="submenu-children">
                    {sub.children.map((child, ci) => (
                      <NavLink key={ci} path={child.path} onClick={closeAll}>{child.name}</NavLink>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink key={i} path={sub.path} onClick={closeAll}>{sub.name}</NavLink>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  const visibleMenu = visibleCount === null ? menu : menu.slice(0, visibleCount);
  const overflowMenu = visibleCount === null ? [] : menu.slice(visibleCount);

  return (
    <>
      <header className="header">
        <div className="hdr-topline">
          <div className="container">
            <div className="hdr-topline-center">
              <ToplineRotator items={topline} />
            </div>
          </div>
        </div>

        <div className="hdr-content">
          <div className="container">
            <div className="header-content" ref={navContainerRef}>

              {/* 1. Hamburger */}
              <button
                className="mobile-toggle"
                onClick={() => { setMobileOpen(o => !o); setOpenItems({}); setOpenGroups({}); }}
                aria-label="Toggle menu"
              >
                {mobileOpen
                  ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                }
              </button>

              {/* 2. Logo */}
              <Link to="/" className="logo" onClick={closeAll} ref={logoRef}>
                {logo ? <img src={logo} alt={siteName} width="220" height="80" onLoad={recalcNavOverflow} onError={e => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'block'); }} /> : null}
                <span className="logo-text" style={{ display: logo ? 'none' : 'block' }}>{siteName}</span>
              </Link>

              {/* 3. Nav + Actions grouped together on the right */}
              <div className="header-right">
              <nav className="nav nav-desktop">
                {visibleMenu.map((item, idx) => renderTopNavItem(item, idx))}

                {overflowMenu.length > 0 && (
                  <div className="nav-item has-submenu nav-item-more">
                    <div className="nav-item-row">
                      <a href="#more" onClick={e => e.preventDefault()}>More</a>
                    </div>
                    <div className="submenu">
                      {overflowMenu.map((item, idx) =>
                        getSubmenu(item).length > 0 ? (
                          <div key={idx} className="submenu-group">
                            <div className="submenu-group-label"><span>{item.name}</span></div>
                            <div className="submenu-children">
                              {getSubmenu(item).map((sub, i) =>
                                sub.children?.length > 0 ? (
                                  sub.children.map((child, ci) => (
                                    <NavLink key={`${i}-${ci}`} path={child.path} onClick={closeAll}>{child.name}</NavLink>
                                  ))
                                ) : (
                                  <NavLink key={i} path={sub.path} onClick={closeAll}>{sub.name}</NavLink>
                                )
                              )}
                            </div>
                          </div>
                        ) : (
                          <NavLink key={idx} path={item.path} onClick={closeAll}>{item.name}</NavLink>
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* Hidden clone of the full list, off-screen — measured to decide how many
                    items actually fit before collapsing the rest into "More". */}
                <div className="nav-measure" ref={measureRef} aria-hidden="true">
                  {menu.map((item, idx) => renderTopNavItem(item, idx))}
                </div>
                <div className="nav-measure" ref={moreMeasureRef} aria-hidden="true">
                  <div className="nav-item has-submenu"><div className="nav-item-row"><a href="#more">More</a></div></div>
                </div>
              </nav>

              {/* 4. Actions */}
              <div className="header-actions" ref={actionsRef}>
                {!isAdmin && headerIcons.showCompare && (
                  <Link to="/compare" className="compare-icon-btn" aria-label="Compare" title="Compare">
                    <span className="compare-icon-wrap">
                      {/* balance scale = compare */}
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 3v18"/>
                        <path d="M8 21h8"/>
                        <path d="M5 6h14"/>
                        <path d="M5 6l-3 7a3 3 0 0 0 6 0L5 6z"/>
                        <path d="M19 6l-3 7a3 3 0 0 0 6 0l-3-7z"/>
                      </svg>
                      {compareList?.length > 0 && <span className="compare-icon-dot" />}
                    </span>
                    <span className="compare-icon-label">Compare</span>
                  </Link>
                )}
                {headerIcons.showUser && (
                  <div className="user-icon-wrap">
                    <button
                      className="user-icon-btn"
                      aria-label={user ? 'Account menu' : 'Sign in'}
                      onClick={() => user ? setUserMenuOpen(o => !o) : navigate('/login')}
                      title={user ? user.name : 'Sign In'}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      {user && <span className="user-icon-dot" />}
                    </button>
                    {user && userMenuOpen && (
                      <>
                        <div className="user-menu-backdrop" onClick={() => setUserMenuOpen(false)} />
                        <div className="user-menu">
                          <div className="user-menu-name">&#128100; {user.name}</div>
                          <Link to="/orders" className="user-menu-item" onClick={() => setUserMenuOpen(false)}> My Orders</Link>
                          <button className="user-menu-item user-menu-signout" onClick={() => { logout(); setUserMenuOpen(false); navigate('/'); }}>&#128682; Sign Out</button>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {headerIcons.showCart && (
                  <button className="cart-icon-btn" onClick={() => openCartDrawer()} aria-label="Open cart">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                      <line x1="3" y1="6" x2="21" y2="6"/>
                      <path d="M16 10a4 4 0 01-8 0"/>
                    </svg>
                    {itemCount > 0 && <span className="cart-icon-badge">{itemCount > 99 ? '99+' : itemCount}</span>}
                  </button>
                )}
                {headerIcons.showSearch && (
                  <button
                    className={`cart-icon-btn search-icon-btn${searchOpen ? ' is-open' : ''}`}
                    onClick={() => setSearchOpen(o => !o)}
                    aria-label={searchOpen ? 'Close search' : 'Search'}
                    aria-expanded={searchOpen}
                    title="Search"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="7" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </button>
                )}
              </div>
              </div>

            </div>

            {/* Mobile nav — outside flex row, rendered below header-content */}
            <nav className={`nav nav-mobile${mobileOpen ? ' nav-open' : ''}`}>
              {menu.map((item, idx) => {
                const subs = getSubmenu(item);
                const hasSub = subs.length > 0;
                const isOpen = !!openItems[idx];
                return (
                  <div key={idx} className={`nav-item${hasSub ? ' has-submenu' : ''}${isOpen ? ' mob-open' : ''}`}>
                    <div className="nav-item-row">
                      <NavLink path={item.path} onClick={closeAll}>{item.name}</NavLink>
                      {hasSub && (
                        <button className="mob-toggle-btn" onClick={(e) => toggleItem(e, idx)} aria-label="expand">
                          {isOpen ? '▲' : '▼'}
                        </button>
                      )}
                    </div>
                    {hasSub && (
                      <div className={`submenu${subs.length > 4 ? ' submenu-mega' : ''}${isOpen ? ' mob-submenu-open' : ''}`} style={subs.length > 4 ? { '--sub-rows': Math.ceil(subs.length / 2) } : undefined}>
                        {subs.map((sub, i) =>
                          sub.children?.length > 0 ? (
                            <div key={i} className={`submenu-group${openGroups[`${idx}-${i}`] ? ' mob-group-open' : ''}`}>
                              <div className="submenu-group-label" onClick={(e) => toggleGroup(e, `${idx}-${i}`)}>
                                <span>{sub.name}</span>
                                <span className="mob-group-arrow">{openGroups[`${idx}-${i}`] ? '▲' : '▼'}</span>
                              </div>
                              <div className="submenu-children">
                                {sub.children.map((child, ci) => (
                                  <NavLink key={ci} path={child.path} onClick={closeAll}>{child.name}</NavLink>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <NavLink key={i} path={sub.path} onClick={closeAll}>{sub.name}</NavLink>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

          </div>
        </div>

        {/* Search bar: drops down below the header row / nav menu (opened by the search icon) */}
        {headerIcons.showSearch && <SiteSearch open={searchOpen} onClose={() => setSearchOpen(false)} menu={menu} />}

        {/* Backdrop outside flex row */}
        {mobileOpen && <div className="mob-nav-backdrop" onClick={closeAll} />}
      </header>

      <CartDrawer open={cartDrawerOpen} onClose={() => closeCartDrawer()} />
    </>
  );
}

export default Header;
